import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { scenes } from '@padcanvas/shared';
import { BackButton, IdControl, ImageViewer } from '@shared-ui/components';
import { LatexText } from '@shared-ui/latex';
import { MechanicsBench } from '@shared-ui/mechanics-bench';
import { OpticsBench } from '@shared-ui/optics-bench';
import { scenesApi, SCENES_QUERY_KEY } from './scenesApi';
import { AGREEMENT_MARK, CANNOT_LABEL, formatReading, useModuleLabel } from './sceneLabels';
import { ConfidencePill, TrustPill } from './ScenePills';
import './Scenes.css';

/**
 * Admin → Scenes, one question: the question on the left, its scene opened on
 * the live bench on the right, and the readings held against the key between
 * them. Accept or reject is the one write.
 *
 * See docs/FeatureDesigns/question-scenes/question-scenes-target.md §6 and §10.
 */

type Doc = scenes.QuestionSceneDoc;

const LEFT_OPEN_KEY = 'scn-question-panel-open';
type SceneVerdict = Extract<scenes.CompileVerdict, { outcome: 'scene' }>;

export function SceneQuestion() {
    const { questionId = '' } = useParams<{ questionId: string }>();
    const queryClient = useQueryClient();
    const moduleLabel = useModuleLabel();
    const detailKey = [SCENES_QUERY_KEY, 'detail', questionId];

    const { data, isLoading, error } = useQuery({
        queryKey: detailKey,
        queryFn: () => scenesApi.detail(questionId),
        enabled: !!questionId,
    });

    const [note, setNote] = useState('');
    /* The question panel folds away so the bench can have the whole width —
       remembered in this browser, since a reviewer working down a chapter
       wants the same layout on every question. Stored access can throw
       (private window, blocked site data), so it is guarded and falls back
       to open. */
    const [leftOpen, setLeftOpen] = useState<boolean>(() => {
        try { return window.localStorage.getItem(LEFT_OPEN_KEY) !== 'false'; } catch { return true; }
    });
    const toggleLeft = (): void => setLeftOpen(open => {
        try { window.localStorage.setItem(LEFT_OPEN_KEY, String(!open)); } catch { /* not remembered, still applied */ }
        return !open;
    });
    const review = useMutation({
        mutationFn: (decision: 'accept' | 'reject') =>
            scenesApi.review(questionId, { decision, ...(note.trim() ? { note: note.trim() } : {}) }),
        onSuccess: (doc) => {
            queryClient.setQueryData<scenes.SceneDetailResponse>(detailKey, prev => prev ? { ...prev, scene: doc } : prev);
            // The chapter's counts and list both moved.
            void queryClient.invalidateQueries({ queryKey: [SCENES_QUERY_KEY, 'coverage'] });
            void queryClient.invalidateQueries({ queryKey: [SCENES_QUERY_KEY, 'list'] });
            setNote('');
        },
    });

    const doc = data?.scene;
    const question = data?.question ?? null;
    const stale = !!(doc && question && question.questionHash !== doc.questionHash);

    return (
        <div className="split-layout scn-q">
            <div className="scn-q-head">
                <BackButton to={doc ? `/scenes?module=${encodeURIComponent(doc.moduleId)}` : '/scenes'} label="Scenes" compact />
                <h1>Scene</h1>
                <span className="scn-dim"><IdControl value={questionId} container="questions" /></span>
                {doc && (
                    <>
                        <span className="scn-dim">· {moduleLabel(doc.moduleId)} · {doc.bench}</span>
                        <TrustPill trust={doc.trust} />
                        {doc.verdict.outcome === 'scene' && <ConfidencePill confidence={doc.verdict.confidence} />}
                        {stale && <span className="scn-stale">stale</span>}
                    </>
                )}
            </div>

            {isLoading && <p className="scn-dim scn-pad">Loading…</p>}
            {error && <p className="scn-error scn-pad">{(error as Error).message}</p>}

            {doc && (
                <div className="scn-q-body">
                    {leftOpen ? (
                    <div className="scn-q-left">
                        <button type="button" className="scn-q-fold" onClick={toggleLeft}
                            aria-expanded="true" aria-label="Hide the question panel" title="Hide the question panel">
                            ‹ Hide
                        </button>
                        {stale && (
                            <p className="scn-banner scn-banner--warn">
                                The question has changed since this scene was compiled — the scene may no longer match it.
                            </p>
                        )}

                        <section className="scn-card">
                            <h3>Question</h3>
                            {question ? <QuestionPanel q={question} /> : (
                                <p className="scn-error">The question no longer exists.</p>
                            )}
                        </section>

                        {doc.verdict.outcome === 'cannot' ? (
                            <section className="scn-card">
                                <h3>No scene</h3>
                                <p><b className="scn-cannot">{CANNOT_LABEL[doc.verdict.reason]}</b></p>
                                <p>{doc.verdict.why}</p>
                            </section>
                        ) : (
                            <SceneDetail doc={doc} verdict={doc.verdict} />
                        )}

                        <section className="scn-card">
                            <h3>Provenance</h3>
                            <dl className="scn-dl scn-dl--wide">
                                <dt>Source</dt><dd>{doc.source}</dd>
                                <dt>Model</dt><dd>{doc.compiledBy.model}</dd>
                                <dt>Prompt</dt><dd><code>{doc.compiledBy.promptVersion}</code></dd>
                                {doc.compiledBy.runId && <><dt>Run</dt><dd><code>{doc.compiledBy.runId}</code></dd></>}
                                <dt>Compiled</dt><dd>{new Date(doc.createdAt).toLocaleString()}</dd>
                                <dt>Updated</dt><dd>{doc._ts ? new Date(doc._ts * 1000).toLocaleString() : '—'}</dd>
                            </dl>
                        </section>

                        <section className="scn-card">
                            <h3>Review</h3>
                            {doc.review && (
                                <p className="scn-dim">
                                    {doc.review.decision === 'accept' ? 'Accepted' : 'Rejected'} by {doc.review.by.slice(0, 8)} on{' '}
                                    {new Date(doc.review.at).toLocaleString()}
                                    {doc.review.note ? ` — “${doc.review.note}”` : ''}
                                </p>
                            )}
                            <textarea
                                className="scn-note"
                                placeholder="Note (optional) — what you checked, or why it is wrong"
                                value={note}
                                onChange={e => setNote(e.target.value)}
                                rows={2}
                            />
                            <div className="scn-actions">
                                <button
                                    className="btn btn-primary"
                                    disabled={review.isPending || doc.verdict.outcome === 'cannot'}
                                    title={doc.verdict.outcome === 'cannot' ? 'There is no scene to accept' : undefined}
                                    onClick={() => review.mutate('accept')}
                                >Accept</button>
                                <button
                                    className="btn btn-secondary"
                                    disabled={review.isPending}
                                    onClick={() => review.mutate('reject')}
                                >Reject</button>
                                {review.isPending && <span className="scn-dim">Saving…</span>}
                            </div>
                            {review.error && <p className="scn-error">{(review.error as Error).message}</p>}
                        </section>
                    </div>
                    ) : (
                        <button type="button" className="scn-q-rail" onClick={toggleLeft}
                            aria-expanded="false" aria-label="Show the question panel" title="Show the question panel">
                            <span>› Question</span>
                        </button>
                    )}

                    <div className="scn-q-bench">
                        {doc.verdict.outcome === 'scene'
                            ? <SceneBench key={`${doc.id}:${doc._ts ?? ''}`} scene={scenes.withAssumed(doc.verdict.scene, doc.check?.assumed)} />
                            : <p className="scn-dim scn-pad">A <i>cannot</i> verdict has no scene to open.</p>}
                    </div>
                </div>
            )}
        </div>
    );
}

function QuestionPanel({ q }: { q: NonNullable<scenes.SceneDetailResponse['question']> }) {
    const options = Array.isArray(q.options)
        ? (q.options as { label?: string; text?: string }[]).filter(o => o && typeof o.text === 'string')
        : [];
    const image = q.questionImage && /^(https?:|data:|blob:|\/)/.test(q.questionImage) ? q.questionImage : undefined;
    return (
        <>
            <LatexText text={q.questionText} />
            {image && <ImageViewer src={image} alt="Question figure" className="scn-qimage" />}
            {q.questionImage && !image && <p className="scn-dim">Figure: <code>{q.questionImage}</code></p>}
            {options.length > 0 && (
                <ol className="scn-options">
                    {options.map((o, i) => (
                        <li key={i}><b>{(o.label ?? String.fromCharCode(97 + i)).toUpperCase()}.</b> <LatexText text={o.text ?? ''} /></li>
                    ))}
                </ol>
            )}
            <div className="scn-answer">
                <span className="scn-dim">Answer key</span>
                {q.correctAnswer ? <LatexText text={q.correctAnswer} /> : <span className="scn-dim">none</span>}
            </div>
        </>
    );
}

/** The trust the gate gave the stored check — what a fresh `checkVerdict` is
 *  compared against. A curated doc's trust is a reviewer's; this is the engine's. */
function storedGateTrust(check: NonNullable<Doc['check']>): scenes.Trust {
    if (check.errors.length || check.agreement.includes('disagrees')) return 'rejected';
    return check.agreement.includes('no-key') ? 'unverified' : 'verified';
}

function sameReading(a: scenes.Reading, b: scenes.Reading): boolean {
    if (typeof a === 'number' && typeof b === 'number') return Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a), Math.abs(b));
    return a === b;
}

function SceneDetail({ doc, verdict }: { doc: Doc; verdict: SceneVerdict }) {
    const scene = verdict.scene;
    const check = doc.check;
    const decls = scenes.benchOf(scene.bench)?.readouts ?? [];

    // Re-solve in the browser: the engine may have changed since the writer ran
    // the gate, and a stored "verified" is only as good as the engine it ran on.
    const live = useMemo(() => {
        try {
            return scenes.checkVerdict(verdict);
        } catch (e) {
            return { errors: [`the engine threw: ${(e as Error).message}`] };
        }
    }, [verdict]);

    const drift = useMemo((): string[] => {
        if (!check) return [];
        if (!('solution' in live)) return live.errors.length ? [`the verdict no longer checks: ${live.errors.join('; ')}`] : [];
        const out: string[] = [];
        const was = storedGateTrust(check);
        if (live.trust !== was) out.push(`trust by the gate was ${was}, is now ${live.trust}`);
        live.solution.answers.forEach((a, i) => {
            const stored = check.answers[i];
            if (!stored || !sameReading(stored.value, a.value)) {
                out.push(`ask ${i + 1} (${a.ask.readout}) was ${formatReading(stored?.value, stored?.unit)}, is now ${formatReading(a.value, a.unit)}`);
            }
        });
        if (check.answers.length !== live.solution.answers.length) out.push('the number of answers changed');
        return out;
    }, [check, live]);

    return (
        <>
            {drift.length > 0 && (
                <div className="scn-banner scn-banner--warn">
                    <b>The engine's answer has changed since this was compiled.</b>
                    <ul>{drift.map((d, i) => <li key={i}>{d}</li>)}</ul>
                </div>
            )}

            <section className="scn-card">
                <h3>Readings vs key</h3>
                {!check && <p className="scn-dim">No stored check.</p>}
                {check && check.errors.length > 0 && (
                    <div className="scn-banner scn-banner--error">
                        <b>The scene does not stand.</b>
                        <ul>{check.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                    </div>
                )}
                <table className="scn-asks">
                    <thead>
                        <tr><th>Part</th><th>Readout</th><th>Bench</th><th>Key</th><th></th></tr>
                    </thead>
                    <tbody>
                        {scene.asks.map((ask, i) => {
                            const decl = decls.find(d => d.key === ask.readout);
                            const answer = check?.answers[i];
                            const exp = verdict.expected.find(e => e.ask === i);
                            const agreement = check?.agreement[i];
                            const disagrees = agreement === 'disagrees';
                            return (
                                <tr key={i} className={disagrees ? 'scn-ask--disagrees' : ''}>
                                    <td>{ask.part ?? '—'}</td>
                                    <td>
                                        {decl?.label ?? ask.readout}
                                        {ask.on !== undefined && <span className="scn-dim"> · on {ask.on}</span>}
                                        <div className="scn-dim scn-small"><code>{ask.readout}</code></div>
                                    </td>
                                    <td className="scn-num">{answer ? formatReading(answer.value, answer.unit) : '—'}</td>
                                    <td className="scn-num">{formatExpected(exp)}</td>
                                    <td>
                                        {agreement && <span className={`scn-mark--${agreement}`} title={agreement}>{AGREEMENT_MARK[agreement]}</span>}
                                        {disagrees && <div className="scn-disagree-label">scene wrong or key wrong?</div>}
                                        {check?.notes[i] && <div className="scn-dim scn-small">{check.notes[i]}</div>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {check?.found !== undefined && scene.inverse && (
                    <p className="scn-dim scn-small">
                        Inverse: found <code>{scene.inverse.param}</code> = {formatReading(check.found)} so that{' '}
                        <code>{scene.inverse.readout}</code> = {scene.inverse.equals}.
                    </p>
                )}
            </section>

            <section className="scn-card">
                <h3>The compiler said</h3>
                <dl className="scn-dl scn-dl--wide">
                    <dt>Confidence</dt>
                    <dd><ConfidencePill confidence={verdict.confidence} /> {verdict.confidenceWhy}</dd>
                    <dt>Fidelity</dt>
                    <dd>
                        {verdict.fidelity}
                        {verdict.approximations && verdict.approximations.length > 0 && (
                            <ul>{verdict.approximations.map((a, i) => <li key={i}>{a}</li>)}</ul>
                        )}
                    </dd>
                    <dt>Assumes</dt>
                    <dd>
                        {scene.assumptions.length === 0 ? <span className="scn-dim">nothing stated</span> : (
                            <ul>{scene.assumptions.map((a, i) => <li key={i}>{a}</li>)}</ul>
                        )}
                    </dd>
                </dl>
                <details className="scn-json">
                    <summary>Setup (spec v{scene.specVersion})</summary>
                    <pre>{JSON.stringify({ setup: scene.setup, asks: scene.asks, ...(scene.inverse ? { inverse: scene.inverse } : {}) }, null, 2)}</pre>
                </details>
            </section>
        </>
    );
}

function formatExpected(e: scenes.Expected | undefined): string {
    if (!e) return 'not in key';
    if (e.absent) return 'no key';
    if (e.category !== undefined) return e.category;
    if (e.value === undefined) return '—';
    return `${e.stated ?? +e.value.toPrecision(4)}${e.unit ? ` ${e.unit}` : ''}`;
}

/** The live bench, opened on the scene. Built once per mount: both benches read
 *  `initialWorld` only when they mount, so the caller keys this by the doc. */
function SceneBench({ scene }: { scene: scenes.Scene }) {
    const built = useMemo((): { mechanics?: ReturnType<typeof scenes.buildMechanics>; optics?: ReturnType<typeof scenes.buildOptics>; error?: string } => {
        try {
            const setup = scenes.solvedSetup(scene);
            if (!setup) return { error: 'The inverse could not be solved, so there is no setup to open.' };
            if (scene.bench === 'mechanics') return { mechanics: scenes.buildMechanics(setup as unknown as scenes.MechanicsSetup) };
            if (scene.bench === 'optics') return { optics: scenes.buildOptics(setup as unknown as scenes.OpticsSetup) };
            return { error: `No bench called "${String(scene.bench)}".` };
        } catch (e) {
            return { error: `The bench could not build this setup: ${(e as Error).message}` };
        }
    }, [scene]);

    if (built.error) return <p className="scn-error scn-pad">{built.error}</p>;
    if (built.mechanics) return <MechanicsBench initialWorld={built.mechanics} />;
    if (built.optics) return <OpticsBench initialWorld={built.optics} />;
    return null;
}
